import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Box, Typography, IconButton, InputAdornment, Alert
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import { QRCodeSVG } from 'qrcode.react';
import { db } from '../firebase';
import { addDoc, collection } from 'firebase/firestore';
import { resolveShareYoutubeId } from '../services/shareService';

interface UnifiedShareDialogProps {
  open: boolean;
  onClose: () => void;
  video: any;
  user: any;
}

const defaultTags = [
  "這支影片對你有幫助",
  "請看重點！",
  "有趣的影片，一起看！",
  "影片找很久，拜託看完",
  "請勿模仿！",
  "真的憋太久了～",
  "可以抽空再看！",
  "我是分享家！",
  "我是 ...",
  "新聞快報！",
  "重要新知！",
  "我們是 (公司名稱）",
  "老闆交待要看兩遍～",
  "讓分享變的有趣！"
];

const getAllTags = (customTags: string[]) => {
  return [...defaultTags, ...customTags.filter(tag => !defaultTags.includes(tag))];
};

const getSSRBaseUrl = () => {
  if (typeof window !== 'undefined' && (window as any).VITE_SSR_BASE_URL) {
    return (window as any).VITE_SSR_BASE_URL;
  }
  return window.location.origin;
};

const UnifiedShareDialog: React.FC<UnifiedShareDialogProps> = ({ open, onClose, video, user }) => {
  const [shareMessage, setShareMessage] = useState('');
  const [shareStep, setShareStep] = useState<'input' | 'display'>('input');
  const [isSavingShare, setIsSavingShare] = useState(false);
  const [currentShareUrl, setCurrentShareUrl] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [newTagInput, setNewTagInput] = useState('');

  useEffect(() => {
    if (open) {
      setShareMessage('');
      setShareStep('input');
      setIsSavingShare(false);
      setCurrentShareUrl('');
      setCopySuccess(false);
      setEditingTag(null);
      setEditingValue('');
      setNewTagInput('');
      if (user?.shareTags) setCustomTags(user.shareTags);
      else setCustomTags([]);
    }
  }, [open, user]);

  const handleSaveShareMessage = async (message: string) => {
    console.log('user:', user);
    console.log('video:', video);
    
    // 移除登入檢查，允許匿名分享
    setIsSavingShare(true);
    try {
      const finalMessage = message && message.trim() ? message : 'CheckPlay，讓分享變的有趣！';
      const youtubeVideoId = resolveShareYoutubeId(video);
      const fallbackId = video?.videoId || video?.id || '';
      const shareData = {
        videoId: youtubeVideoId || fallbackId,
        youtubeVideoId: youtubeVideoId || null,
        fromUser: user?.email || 'Guest', // 如果沒有登入，使用 'Guest'
        message: finalMessage,
        createdAt: new Date().toISOString(),
        originalVideoData: video || null,
        isGuestShare: !user?.email // 標記是否為匿名分享
      };
      const docRef = await addDoc(collection(db, 'sharedLinks'), shareData);
      const ssrBaseUrl = getSSRBaseUrl();
      const newShareUrl = `${ssrBaseUrl}/main/share/${docRef.id}`;
      setCurrentShareUrl(newShareUrl);
      setShareStep('display');
    } catch (e) {
      alert('分享失敗，請稍後再試');
    } finally {
      setIsSavingShare(false);
    }
  };

  const handleConfirmShare = () => {
    handleSaveShareMessage(shareMessage);
  };

  const handleSkipShare = () => {
    const defaultMessage = 'CheckPlay，讓分享變的有趣！';
    handleSaveShareMessage(defaultMessage);
  };

  const handleAddCustomTag = () => {
    const tag = newTagInput.trim();
    if (!tag || customTags.includes(tag) || defaultTags.includes(tag)) return;
    setCustomTags([...customTags, tag]);
    setNewTagInput('');
    // 可同步寫入 Firestore user 設定
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, p: 1.5, minWidth: 320, width: '100%', maxWidth: 380 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <ShareIcon color="primary" sx={{ fontSize: 24 }} />
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, fontSize: 18 }}>
          CheckPlay分享連結
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* 步驟 1: 輸入分享訊息 */}
        {shareStep === 'input' && (
          <Box sx={{
            width: '100%',
            mb: 2,
            p: 2,
            borderRadius: 3,
            boxShadow: '0 4px 24px rgba(25, 118, 210, 0.10)',
            background: '#fafdff',
            border: '1.5px solid #e3eaf5',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 700, fontSize: 16 }}>
              讓分享變有趣（選填）
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="請輸入您想分享的說明文字..."
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ mb: 2, background: '#fff', borderRadius: 2, boxShadow: '0 1px 4px rgba(25,118,210,0.04)' }}
            />
            {/* Tag 標籤列 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, width: '100%', justifyContent: 'center' }}>
              {getAllTags(customTags).map((tag, idx) => {
                const isEditing = editingTag === tag;
                const isDefault = defaultTags.includes(tag);
                return (
                  <Box key={tag} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isEditing && !isDefault ? (
                      <TextField
                        value={editingValue}
                        size="small"
                        autoFocus
                        onChange={e => setEditingValue(e.target.value)}
                        onBlur={() => {
                          if (editingValue.trim() && editingValue !== tag && !customTags.includes(editingValue) && !defaultTags.includes(editingValue)) {
                            const updated = customTags.map(t => t === tag ? editingValue.trim() : t);
                            setCustomTags(updated);
                            setEditingTag(null);
                            setEditingValue('');
                          } else {
                            setEditingTag(null);
                            setEditingValue('');
                          }
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            if (editingValue.trim() && editingValue !== tag && !customTags.includes(editingValue) && !defaultTags.includes(editingValue)) {
                              const updated = customTags.map(t => t === tag ? editingValue.trim() : t);
                              setCustomTags(updated);
                              setEditingTag(null);
                              setEditingValue('');
                            } else {
                              setEditingTag(null);
                              setEditingValue('');
                            }
                          }
                        }}
                        sx={{ width: 100, mr: 0.5 }}
                      />
                    ) :
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          borderRadius: '12px',
                          px: 1,
                          py: 0.1,
                          minWidth: 0,
                          minHeight: 0,
                          fontWeight: 500,
                          fontSize: 14,
                          color: isDefault ? '#1976d2' : '#1976d2',
                          borderColor: isDefault ? '#90caf9' : '#90caf9',
                          background: isDefault ? '#f0f7ff' : '#f0f7ff',
                          boxShadow: '0 1px 4px rgba(25,118,210,0.04)',
                          lineHeight: 1.2,
                          '&:hover': {
                            background: '#e3f2fd',
                            borderColor: '#1976d2',
                          },
                        }}
                        onClick={() => setShareMessage(tag)}
                        onDoubleClick={() => {
                          if (!isDefault) {
                            setEditingTag(tag);
                            setEditingValue(tag);
                          }
                        }}
                        disabled={isDefault && isEditing}
                      >
                        {tag}
                      </Button>
                    }
                    {/* 只有在編輯模式且非 defaultTags 才顯示刪除X */}
                    {isEditing && !isDefault && (
                      <IconButton
                        size="small"
                        color="error"
                        sx={{ ml: 0, p: 0.5 }}
                        onClick={() => {
                          setCustomTags(customTags.filter(t => t !== tag));
                          setEditingTag(null);
                          setEditingValue('');
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
            {/* 自訂標籤輸入列 */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, width: '100%', justifyContent: 'center' }}>
              <TextField
                value={newTagInput}
                onChange={e => setNewTagInput(e.target.value)}
                size="small"
                placeholder="新增自訂標籤..."
                sx={{ width: 180, background: '#fff', borderRadius: 2 }}
                onKeyDown={e => { if (e.key === 'Enter') handleAddCustomTag(); }}
              />
              <Button
                variant="contained"
                size="small"
                sx={{ fontWeight: 700, minWidth: 60 }}
                onClick={handleAddCustomTag}
                disabled={!newTagInput.trim() || getAllTags(customTags).includes(newTagInput.trim())}
              >
                新增
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', width: '100%' }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSkipShare}
                disabled={isSavingShare}
                sx={{ minWidth: 90, fontWeight: 700 }}
              >
                略過
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirmShare}
                disabled={isSavingShare}
                startIcon={isSavingShare ? <CircularProgress size={16} /> : null}
                sx={{ minWidth: 90, fontWeight: 700 }}
              >
                確認
              </Button>
            </Box>
          </Box>
        )}

        {/* 步驟 2: 顯示 QR Code 與分享網址 */}
        {shareStep === 'display' && currentShareUrl && (
          <>
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <QRCodeSVG
                value={currentShareUrl}
                size={180}
                level="M"
                includeMargin={true}
                style={{
                  padding: '8px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                掃描 QR Code 以分享你的 Check Points
              </Typography>
            </Box>
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                分享網址
              </Typography>
              <Box
                sx={{
                  background: '#f5f7fa',
                  borderRadius: 1,
                  p: 1,
                  fontSize: '0.95rem',
                  wordBreak: 'break-all',
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <span style={{ flex: 1 }}>{currentShareUrl}</span>
                <IconButton
                  size="small"
                  aria-label="複製分享網址"
                  onClick={async () => {
                    await navigator.clipboard.writeText(currentShareUrl);
                    setCopySuccess(true);
                    onClose(); // 複製後自動關閉 Dialog
                  }}
                  sx={{ ml: 1 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                點擊右側圖示可「複製分享網址」
              </Typography>
              {copySuccess && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  完成複製，登入帳號開始管理影片重點！
                </Alert>
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedShareDialog; 